import { useState, lazy, Suspense } from "react";
import { Add } from "iconsax-react";
import { Button } from "@/shared/ui";
import { useAuth } from "@/shared/hooks";
import { useCalculationsPage } from "../../hooks";
import { CalculationsPageSkeleton } from "../../components";
import type { GphCompletionAct } from "../../types";
import { ContractDashboard, ServiceItemsBlock, CalculationsHistoryBlock } from "./components";
import MSBCalculationsPage from "../MSBCalculationsPage";

const CompletionActCreateModal = lazy(() =>
  import("../../components/CompletionActCreateModal").then((m) => ({ default: m.default })),
);

export default function CalculationsPage() {
  const { user } = useAuth();
  if (user?.user_type === "msb_contractor") return <MSBCalculationsPage />;
  return <GphCalculationsPage />;
}

function GphCalculationsPage() {
  const { contract, isLoading, isNotFound, summary, serviceItemSummaries, sortedActs, createModalOpen, setCreateModalOpen, t, locale } =
    useCalculationsPage();

  const [editAct, setEditAct] = useState<GphCompletionAct | null>(null);

  function handleEdit(act: GphCompletionAct) {
    setEditAct(act);
  }

  function handleEditClose() {
    setEditAct(null);
  }

  if (isLoading && !contract) {
    return (
      <>
        <title>{t("meta.calculationsTitle")}</title>
        <section className="surface-base-fill flex-1">
            <div className="flex items-center justify-between pb-4 pt-1 border-b surface-base-stroke mb-5">
              <h1 className="text-display-xs content-base-primary">{t("calculations.title")}</h1>
            </div>
            <CalculationsPageSkeleton />
        </section>
      </>
    );
  }

  return (
    <>
      <title>{t("meta.calculationsTitle")}</title>
      <section className="flex-1">
          <div className="flex items-center justify-between pb-4 pt-1 border-b surface-base-stroke mb-5">
            <h1 className="text-display-xs content-base-primary">{t("calculations.title")}</h1>
            {contract && (
              <Button variant="primary" size="lg" onClick={() => setCreateModalOpen(true)}>
                <Add size={16} color="currentColor" />
                {t("calculations.generateAvr")}
              </Button>
            )}
          </div>

          {isNotFound || !contract ? (
            <div className="flex flex-col gap-5 p-5 radius-lg border surface-component-stroke">
              <p className="text-body-regular-md content-action-neutral">{t("calculations.noContract")}</p>
            </div>
          ) : (
            <>
              <ContractDashboard contract={contract} summary={summary} locale={locale} />
              <ServiceItemsBlock items={serviceItemSummaries} locale={locale} />
              <CalculationsHistoryBlock acts={sortedActs} locale={locale} onEdit={handleEdit} />
            </>
          )}
      </section>

      {createModalOpen && contract && (
        <Suspense fallback={null}>
          <CompletionActCreateModal
            onClose={() => setCreateModalOpen(false)}
            contract={contract}
            onSuccess={() => setCreateModalOpen(false)}
          />
        </Suspense>
      )}

      {editAct && contract && (
        <Suspense fallback={null}>
          <CompletionActCreateModal
            onClose={handleEditClose}
            contract={contract}
            onSuccess={handleEditClose}
            editAct={editAct}
          />
        </Suspense>
      )}
    </>
  );
}
